<template>
  <div v-if="isParcel" class="pickup-block">
    <div class="label fs-xl">Pickup</div>
    <template v-if="autoPickupEnabled">
      <PickupCalendar :pickup-error="error" :max-period="maxPickupPeriod" :dates="pickupDates" @select-date="setDate" />

      <b-form-group class="mt-5 pickup-instructions">
        <label>Pickup Instructions</label>
        <div class="input-group">
          <input
            v-model="pickupInstructions"
            type="text"
            placeholder="Eg. Pickup behind the little small red house"
            class="form-control"
          />
        </div>
      </b-form-group>
    </template>
    <template v-else>
      <InfoAlert class="secondary mt-3 mb-4">
        Automatic pickup is not enabled for your company, but you will be able to book pickup manually.
      </InfoAlert>
    </template>
  </div>
</template>

<script lang="ts">
import type { AssignmentCreationData } from '@/types/Models/Assignment'
import type { PickupDate } from '@/types/Models/Pickup'
import { BFormGroup } from 'bootstrap-vue-next'
import { ref, defineComponent, onBeforeMount, watch, computed, type Ref, type ComputedRef } from 'vue'
import { PARCEL } from '@/const/DistributionChannels'
import { useAssignmentFormStore } from '@/store/assignmentForm'
import { useCustomersStore } from '@/store/customers'
import InfoAlert from '@/views/Components/PageParts/InfoAlert.vue'
import PickupCalendar from '@/views/Components/Assignment/PickupCalendar.vue'

export default defineComponent({
  name: 'PickupBlock',
  components: { BFormGroup, InfoAlert, PickupCalendar },
  setup() {
    const assignmentFormStore = useAssignmentFormStore()
    const customersStore = useCustomersStore()

    const assignment: ComputedRef<AssignmentCreationData> = computed(() => assignmentFormStore.assignment)
    const companyId: ComputedRef<string> = computed(() => assignment.value.company_id || '')
    const autoPickupEnabled: ComputedRef<boolean> = computed(
      () => customersStore.customer?.autoPickupForParcelShipments ?? false,
    )
    const isParcel: ComputedRef<boolean> = computed(() => assignment.value.distribution_channel === PARCEL)
    // DHL Express max pickup date period is 10 days after current day
    // DHL Freight max pickup date period is 100 days after current day
    const maxPickupPeriod: ComputedRef<number> = computed(() =>
      assignment.value.item.country_code !== 'SE' ? 10 : 100,
    )
    const error: ComputedRef<boolean> = computed(() => assignmentFormStore.errors.pickup_date)
    const pickupDates: ComputedRef<PickupDate[]> = computed(() => assignmentFormStore.pickupDates)

    const pickupInstructions: Ref<string> = ref('')

    watch(pickupInstructions, (value) => assignmentFormStore.setFields({ pickup_instructions: value }))

    watch(assignment, () => {
      if (assignment.value.pickup_instructions !== pickupInstructions.value) {
        pickupInstructions.value = assignment.value.pickup_instructions
      }
    })

    watch(
      () => autoPickupEnabled.value,
      (value) => {
        if (value) return
        assignmentFormStore.setFields({ pickup_date: '', pickup_instructions: '' })
        pickupInstructions.value = ''
      },
    )

    watch(
      () => companyId.value,
      (value) => {
        if (!value) return
        customersStore.fetch(value)
      },
      { immediate: true },
    )

    const setDate = (value: string) => assignmentFormStore.setFields({ pickup_date: value })

    onBeforeMount(() => assignmentFormStore.fetchDates())

    return {
      error,
      autoPickupEnabled,
      isParcel,
      pickupDates,
      maxPickupPeriod,
      pickupInstructions,
      setDate,
    }
  },
})
</script>
